import React from "react";
import { Box, Link, Typography } from "@mui/material";
import { useRouter } from "../../contexts/RouterContext";
import { useAuth } from "../../contexts/AuthContext";

/**
 * Navegação minimalista entre os relatórios de atendimento (Análise, Cancelamentos, Evolutivo,
 * Novos e evasão): uma linha de links abaixo dos filtros, com a página atual em destaque e sem link.
 * Só aparecem os relatórios liberados ao perfil do usuário.
 */
export type ReportSiblingPage =
  | "report-schedule-analysis"
  | "report-cancellations"
  | "report-evolution"
  | "report-patient-flow";

interface ReportSiblingNavProps {
  current: ReportSiblingPage;
  /** competência (AAAA-MM) e período do filtro atual, levados para o relatório de destino quando ele usa */
  competence?: string;
  from?: string;
  to?: string;
}

const PAGES: { page: ReportSiblingPage; label: string }[] = [
  { page: "report-schedule-analysis", label: "Análise de Atendimentos" },
  { page: "report-cancellations", label: "Cancelamentos" },
  { page: "report-evolution", label: "Evolutivo" },
  { page: "report-patient-flow", label: "Novos e evasão" },
];

const monthBounds = (competence: string): { from: string; to: string } => {
  const [year, month] = competence.split("-").map(Number);
  const last = new Date(year, month, 0).getDate();
  return { from: `${competence}-01`, to: `${competence}-${String(last).padStart(2, "0")}` };
};

const ReportSiblingNav: React.FC<ReportSiblingNavProps> = ({ current, competence, from, to }) => {
  const { navigateTo } = useRouter();
  const { user } = useAuth();
  const allowed = user?.allowedPages || [];
  const pages = PAGES.filter((p) => p.page === current || allowed.includes(p.page));

  const paramsFor = (page: ReportSiblingPage): Record<string, string> | undefined => {
    if (page === "report-evolution" || !competence) return undefined;
    if (page === "report-patient-flow") return { competence };
    const bounds = from && to ? { from, to } : monthBounds(competence);
    return { competence, ...bounds };
  };

  if (pages.length < 2) return null;

  return (
    <Box
      component="nav"
      aria-label="Relatórios de atendimento"
      className="no-print"
      sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", columnGap: 1, rowGap: 0.5, mb: 2, fontSize: 13, color: "#6c757d" }}
    >
      {pages.map((p, index) => (
        <React.Fragment key={p.page}>
          {index > 0 && <span aria-hidden="true">·</span>}
          {p.page === current ? (
            <Typography component="span" aria-current="page" sx={{ fontSize: 13, fontWeight: 600, color: "#2D3748" }}>
              {p.label}
            </Typography>
          ) : (
            <Link component="button" type="button" underline="hover" sx={{ fontSize: 13 }} onClick={() => navigateTo(p.page, paramsFor(p.page))}>
              {p.label}
            </Link>
          )}
        </React.Fragment>
      ))}
    </Box>
  );
};

export default ReportSiblingNav;
